import type { AxisSelection } from '@atlas/types'
import { ClassifiedObjectType, FileProcessingConcernId } from '@atlas/types'
import type { ClassifyTuple } from '@/services/classification.service'
import type { FileDTO } from '@/services/file.service'
import { enqueue } from './upload-store'

/** The dimension tuple as classify tuples (attributes like `order_code` are set separately). */
export function tupleTags(fileId: string, tuple: AxisSelection): ClassifyTuple[] {
	const tags: ClassifyTuple[] = []
	for (const [axisCode, codes] of Object.entries(tuple)) {
		for (const valueCode of codes) {
			tags.push({ objectType: ClassifiedObjectType.FILE, objectId: fileId, axisCode, valueCode })
		}
	}
	return tags
}

export interface ClassifiedUploadHandlers {
	/** Work that must happen once classification is terminal, whatever its outcome. */
	afterClassified?: (file: FileDTO) => Promise<void> | void
	/** `afterClassified` threw. The upload itself already succeeded. */
	onFollowUpError?: (error: unknown) => void
	/** The `classification` concern failed server-side. Retryable from the tile. */
	onClassificationFailed?: (errorMessage: string | undefined) => void
	/** Tags the item so one drop zone renders only its own uploads. */
	owner?: string
	/** `false` creates the file inactive, as a draft design file. */
	isActive?: boolean
}

/**
 * Enqueues a file whose classification depends on the server-assigned file id.
 *
 * The tuples can't be built before the upload starts, because every tuple names
 * the file it classifies. So the caller supplies a *builder* and this defers it
 * to the moment the id exists — which the store then hands to `confirmUpload`,
 * where it becomes a tracked, retryable `classification` concern rather than a
 * separate fire-and-forget `classifyBatch` call.
 *
 * **The returned promise settles at confirm, not at `SETTLED`.** The caller is a
 * react-query mutation whose job is "was the upload accepted?" — gating it on
 * every concern would leave the button spinning through a virus scan and a
 * thumbnail render, and would fail the mutation because of an unrelated
 * concern's failure.
 *
 * `afterClassified` runs once the `classification` concern reaches a terminal
 * state, for follow-ups that read the file's own classification back (ordering,
 * audit). It hangs off that one concern rather than off `SETTLED` because
 * nothing it does depends on a thumbnail.
 *
 * **It runs on failure too.** These follow-ups (`order_code`, `recordFileAttached`)
 * are what make an uploaded file visible to its work order at all — skipping
 * them when classification fails leaves the file stored but attached to nothing,
 * which is worse than an un-classified file. So they run regardless, and
 * `onClassificationFailed` reports the classification problem separately.
 */
export function enqueueClassified(
	file: File,
	buildTuples: (fileId: string) => ClassifyTuple[],
	handlers: ClassifiedUploadHandlers = {},
): Promise<FileDTO> {
	const { afterClassified, onFollowUpError, onClassificationFailed, owner, isActive } = handlers
	// Registered whenever either handler is supplied — `onClassificationFailed`
	// alone is a valid use (a flow whose only post-upload work IS the tagging).
	const needsConcernCallback = Boolean(afterClassified || onClassificationFailed)

	return new Promise((resolve, reject) => {
		enqueue(file, {
			// The store resolves the id before confirm, so a builder closed over it
			// is the only way tuples can reference the file they classify.
			buildClassify: buildTuples,
			owner,
			isActive,
			onConfirmed: resolve,
			onConcernSettled: needsConcernCallback
				? {
						concernId: FileProcessingConcernId.CLASSIFICATION,
						run: async (outcome) => {
							// A `failed` classification still gets the attachment work done, so
							// the file isn't orphaned; the user is told tagging failed and can
							// retry just that concern from the tile.
							if (!outcome.ok && outcome.reason === 'failed') onClassificationFailed?.(outcome.errorMessage)
							try {
								await afterClassified?.(outcome.file)
							} catch (error) {
								onFollowUpError?.(error)
							}
						},
					}
				: undefined,
			onFailed: (message) => reject(new Error(message)),
		})
	})
}
